import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Clock, User, MessageSquare, CheckCircle2, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Toaster, toast } from "sonner";
import { createBooking, getTakenSlots } from "../lib/api";

const DAY_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABEL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
];

const STEP_LABELS = ["Date", "Time", "Details", "Needs", "Done"];

const INITIAL_FORM = Object.freeze({
  name: "",
  phone: "",
  email: "",
  date: "",
  time: "",
  for_whom: "self",
  help_with: "",
  preferred_contact: "phone",
  accessibility_needs: "",
});

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isValidEmail = (v) => /.+@.+\..+/.test(v);

const stepBadgeClasses = (idx, step) => {
  if (idx === step) return "bg-forest text-cream";
  if (idx < step) return "bg-daisy text-forest";
  return "bg-forest/10 text-forest";
};

const dayCellClasses = (selected, disabled) => {
  if (selected) return "bg-forest text-cream";
  if (disabled) return "bg-cream text-forest/30 cursor-not-allowed";
  return "bg-cream hover:bg-daisy/40 text-forest border border-forest/10";
};

const slotClasses = (selected, taken) => {
  if (selected) return "bg-forest text-cream border-forest";
  if (taken) return "bg-cream text-forest/30 line-through border-transparent";
  return "bg-white text-forest border-forest/15 hover:bg-daisy/40";
};

/* -------------------------------- Stepper -------------------------------- */
const Stepper = ({ step }) => (
  <ol className="flex items-center justify-between gap-2 mb-8" data-testid="booking-stepper">
    {STEP_LABELS.map((label, i) => {
      const idx = i + 1;
      const done = idx < step;
      const active = idx === step;
      return (
        <li key={label} className="flex-1 flex items-center gap-2">
          <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center font-bold text-sm ${stepBadgeClasses(idx, step)}`}>
            {done ? "✓" : idx}
          </span>
          <span className={`hidden sm:inline text-sm font-semibold ${active ? "text-forest" : "text-forest/60"}`}>{label}</span>
          {i < STEP_LABELS.length - 1 && <span className="flex-1 h-[2px] bg-forest/10" />}
        </li>
      );
    })}
  </ol>
);

/* -------------------------------- Calendar grid -------------------------------- */
const buildMonthGrid = (viewDate) => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
  }
  return cells;
};

const DayCell = ({ day, selectedDate, today, onPick }) => {
  if (!day) return <div />;
  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
  const isPast = day < today;
  const disabled = isPast || isWeekend;
  const selected = selectedDate && fmtDate(day) === selectedDate;
  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={`cal-day-${fmtDate(day)}`}
      onClick={() => onPick(fmtDate(day))}
      className={`h-12 rounded-xl font-semibold text-base transition-colors ${dayCellClasses(selected, disabled)}`}
    >
      {day.getDate()}
    </button>
  );
};

const DatePicker = ({ value, onChange }) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const grid = useMemo(() => buildMonthGrid(view), [view]);

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  return (
    <div data-testid="booking-date-picker">
      <div className="flex items-center justify-between mb-4">
        <button type="button" aria-label="Previous month" onClick={prevMonth} data-testid="cal-prev"
          className="p-2 rounded-full hover:bg-forest/10 text-forest">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="font-serif text-2xl text-forest">
          {MONTH_LABEL[view.getMonth()]} {view.getFullYear()}
        </div>
        <button type="button" aria-label="Next month" onClick={nextMonth} data-testid="cal-next"
          className="p-2 rounded-full hover:bg-forest/10 text-forest">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 text-center text-sm font-semibold text-forest/70 mb-2">
        {DAY_LABEL.map((d) => <div key={d}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {grid.map((day, i) => (
          <DayCell
            key={day ? fmtDate(day) : `blank-${i}`}
            day={day}
            selectedDate={value}
            today={today}
            onPick={onChange}
          />
        ))}
      </div>

      <p className="mt-3 text-sm text-forest/70">
        Weekends shown as unavailable. Pick any weekday that works for you.
      </p>
    </div>
  );
};

/* -------------------------------- Time picker -------------------------------- */
const TimePicker = ({ date, value, onChange }) => {
  const [taken, setTaken] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!date) return undefined;
    setLoading(true);
    getTakenSlots(date)
      .then((slots) => { if (!cancelled) setTaken(slots); })
      .catch(() => { if (!cancelled) setTaken([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  if (loading) {
    return (
      <div data-testid="booking-time-picker">
        <p className="text-forest/80 mb-4">Pick a time on <span className="font-semibold text-forest">{date}</span>.</p>
        <p className="text-forest/70">Loading available times…</p>
      </div>
    );
  }

  return (
    <div data-testid="booking-time-picker">
      <p className="text-forest/80 mb-4">Pick a time on <span className="font-semibold text-forest">{date}</span>.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {TIME_SLOTS.map((slot) => {
          const isTaken = taken.includes(slot);
          const selected = value === slot;
          return (
            <button
              key={slot}
              type="button"
              disabled={isTaken}
              onClick={() => onChange(slot)}
              data-testid={`time-slot-${slot}`}
              className={`h-14 rounded-2xl font-semibold text-lg border-2 transition-colors ${slotClasses(selected, isTaken)}`}
            >
              {slot}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* -------------------------------- Form bits -------------------------------- */
const Field = ({ label, children, required, hint }) => (
  <label className="block">
    <span className="text-base font-semibold text-forest">
      {label} {required && <span className="text-forest/60">*</span>}
    </span>
    {hint && <span className="block text-sm text-forest/60 mb-1">{hint}</span>}
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls =
  "w-full h-14 px-4 rounded-2xl border-2 border-forest/15 bg-white focus:outline-none focus:border-forest text-lg";

const ChoiceGroup = ({ value, onChange, options, testIdPrefix }) => (
  <div className="flex gap-2 flex-wrap">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        data-testid={`${testIdPrefix}-${opt.value}`}
        className={`px-5 h-12 rounded-full font-semibold capitalize border-2 ${
          value === opt.value ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const CONTACT_OPTIONS = [
  { value: "phone", label: "phone" },
  { value: "email", label: "email" },
  { value: "either", label: "either" },
];

const FOR_WHOM_OPTIONS = [
  { value: "self", label: "For me" },
  { value: "someone_else", label: "For someone I care for" },
];

const DetailsStep = ({ form, setField }) => (
  <div className="grid gap-5" data-testid="booking-details-form">
    <Field label="Your name" required>
      <input className={inputCls} value={form.name} onChange={(e) => setField("name", e.target.value)}
        data-testid="booking-name" placeholder="e.g. Margaret Smith" />
    </Field>
    <Field label="Phone number" required>
      <input className={inputCls} value={form.phone} onChange={(e) => setField("phone", e.target.value)}
        data-testid="booking-phone" placeholder="07…" inputMode="tel" />
    </Field>
    <Field label="Email address" required>
      <input className={inputCls} type="email" value={form.email}
        onChange={(e) => setField("email", e.target.value)}
        data-testid="booking-email" placeholder="you@example.com" />
    </Field>
    <Field label="Preferred contact method">
      <ChoiceGroup
        value={form.preferred_contact}
        onChange={(v) => setField("preferred_contact", v)}
        options={CONTACT_OPTIONS}
        testIdPrefix="booking-contact"
      />
    </Field>
  </div>
);

const NeedsStep = ({ form, setField }) => (
  <div className="grid gap-5" data-testid="booking-needs-form">
    <Field label="Is this for you or someone else?">
      <ChoiceGroup
        value={form.for_whom}
        onChange={(v) => setField("for_whom", v)}
        options={FOR_WHOM_OPTIONS}
        testIdPrefix="booking-forwhom"
      />
    </Field>
    <Field label="What do you need help with?" hint="A short sentence is plenty — you don't need to have everything ready.">
      <textarea
        className="w-full min-h-[140px] p-4 rounded-2xl border-2 border-forest/15 bg-white focus:outline-none focus:border-forest text-lg"
        value={form.help_with}
        onChange={(e) => setField("help_with", e.target.value)}
        data-testid="booking-help-with"
        placeholder="e.g. I'd like help understanding an Attendance Allowance form for my mum."
      />
    </Field>
    <Field label="Any accessibility or communication needs?" hint="Optional. Tell Sarah how to make the call easier for you.">
      <input
        className={inputCls}
        value={form.accessibility_needs}
        onChange={(e) => setField("accessibility_needs", e.target.value)}
        data-testid="booking-accessibility"
        placeholder="Optional"
      />
    </Field>
  </div>
);

const Confirmation = ({ form }) => {
  const firstName = form.name.split(" ")[0] || "friend";
  return (
    <div className="text-center py-6" data-testid="booking-confirmation">
      <div className="mx-auto w-16 h-16 rounded-full bg-daisy grid place-items-center mb-4">
        <CheckCircle2 className="w-9 h-9 text-forest" />
      </div>
      <h3 className="font-serif text-3xl text-forest mb-2">Thank you, {firstName} — your call is booked.</h3>
      <p className="text-forest/80 text-lg max-w-lg mx-auto">
        Sarah will be in touch on <span className="font-semibold">{form.date}</span> at <span className="font-semibold">{form.time}</span>.
        You don't need to prepare anything. If anything changes, just give Sarah a call.
      </p>
      <a href="tel:+447771361508" className="btn-secondary mt-6" data-testid="booking-call-sarah">
        <Phone className="w-5 h-5" /> Call Sarah now
      </a>
    </div>
  );
};

/* -------------------------------- Step header -------------------------------- */
const StepHeader = ({ icon: Icon, title, subtitle }) => (
  <>
    <h3 className="font-serif text-2xl sm:text-3xl text-forest mb-2 flex items-center gap-2">
      <Icon className="w-7 h-7" /> {title}
    </h3>
    {subtitle && <p className="text-forest/80 mb-6">{subtitle}</p>}
  </>
);

/* -------------------------------- Validation per step -------------------------------- */
const isStepValid = (step, form) => {
  if (step === 1) return Boolean(form.date);
  if (step === 2) return Boolean(form.time);
  if (step === 3) return Boolean(form.name.trim() && form.phone.trim() && isValidEmail(form.email));
  if (step === 4) return true;
  return false;
};

/* -------------------------------- Step body + nav (extracted) -------------------------------- */
const STEP_BODY = {
  1: ({ form, setField }) => (
    <>
      <StepHeader icon={Calendar} title="Choose a date" subtitle="Free 15-minute introductory call. No pressure, no obligation." />
      <DatePicker value={form.date} onChange={(d) => setField("date", d)} />
    </>
  ),
  2: ({ form, setField }) => (
    <>
      <StepHeader icon={Clock} title="Choose a time" />
      <TimePicker date={form.date} value={form.time} onChange={(t) => setField("time", t)} />
    </>
  ),
  3: ({ form, setField }) => (
    <>
      <StepHeader icon={User} title="Your details" subtitle="Just so Sarah knows who's calling." />
      <DetailsStep form={form} setField={setField} />
    </>
  ),
  4: ({ form, setField }) => (
    <>
      <StepHeader icon={MessageSquare} title="A quick note" subtitle="Tell Sarah what you'd like help with — a short note is plenty." />
      <NeedsStep form={form} setField={setField} />
    </>
  ),
  5: ({ form }) => <Confirmation form={form} />,
};

const BookingStepBody = ({ step, form, setField }) => {
  const Body = STEP_BODY[step];
  return Body ? <Body form={form} setField={setField} /> : null;
};

const BookingNav = ({ step, canContinue, submitting, onBack, onNext, onSubmit }) => (
  <div className="mt-8 flex items-center justify-between gap-3">
    <button
      type="button"
      className="h-12 px-5 rounded-full font-semibold text-forest hover:bg-forest/10 disabled:opacity-40"
      onClick={onBack}
      disabled={step === 1}
      data-testid="booking-back"
    >
      ← Back
    </button>

    {step < 4 ? (
      <button type="button" className="btn-primary" disabled={!canContinue} onClick={onNext} data-testid="booking-next">
        Continue →
      </button>
    ) : (
      <button type="button" className="btn-primary" onClick={onSubmit} disabled={submitting} data-testid="booking-submit">
        {submitting ? "Booking…" : "Confirm my free call"}
      </button>
    )}
  </div>
);

/* -------------------------------- Main flow -------------------------------- */
const BookingFlow = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);
  const goNext = useCallback(() => setStep((s) => s + 1), []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      await createBooking(form);
      toast.success("Your free call is booked. Sarah will be in touch.");
      setStep(5);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again or call Sarah.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  return (
    <div className="soft-card p-6 sm:p-10 relative overflow-hidden" data-testid="booking-flow">
      <div className="yellow-square w-24 h-24 -top-6 -right-6 opacity-90" />
      <Toaster richColors position="top-center" />

      <div className="relative">
        <Stepper step={step} />
        <div className="min-h-[320px]">
          <BookingStepBody step={step} form={form} setField={setField} />
        </div>
        {step < 5 && (
          <BookingNav
            step={step}
            canContinue={isStepValid(step, form)}
            submitting={submitting}
            onBack={goBack}
            onNext={goNext}
            onSubmit={submit}
          />
        )}
      </div>
    </div>
  );
};

export default BookingFlow;
